import { merge } from 'lodash';
import { CellRendererParams, ColumnDef } from '../../models';
import { ArrayPoint } from '../../../cartesian';
import { SparklineCellRenderer } from '../../../cartesian/SparklineCellRenderer';
import { toNumber } from '../number/utils';
import { DEFAULT_EMPTY_CELL_PLACEHOLDER } from '../../constants';
import { parseDateTime } from '../../../../../utils';

export const TimeseriesCellRenderer = <TData, TValue>(props: CellRendererParams<TData, TValue>): JSX.Element => {
  const { value, getValue } = props;
  const colDef = props.colDef as ColumnDef<TData, TValue>;

  const isValueFormatterEnabled = colDef?.valueFormatterOptions?.enabled;
  const isValueArray = Array.isArray(value);
  const hasInvalidTimestamps = isValueArray && value.length > 0 && !('timestamp' in value[0]);
  const shouldRenderDefault = !isValueFormatterEnabled || !isValueArray || hasInvalidTimestamps;

  if (shouldRenderDefault) {
    const displayValue = value != null ? JSON.stringify(value, null, 2) : DEFAULT_EMPTY_CELL_PLACEHOLDER;
    return <div>{displayValue}</div>;
  }

  const data: ArrayPoint[] =
    value?.map(({ timestamp, ...values }) => [Date.parse(timestamp), ...Object.values(values)] as ArrayPoint) ?? [];
  const summaryValue = getValue && getValue() != null ? toNumber(getValue()) : null;
  const summaryDisplayValue = summaryValue != null ? summaryValue : undefined;
  const metadata = colDef.cellRendererParams?.metadata;

  const defaultCellTypeOptions =
    metadata?.since || metadata?.until
      ? {
          highchartsConfig: {
            xAxis: {
              min: metadata.since ? parseDateTime(metadata.since) : undefined,
              max: metadata.until ? parseDateTime(metadata.until) : undefined,
            },
          },
        }
      : {};

  return (
    <SparklineCellRenderer
      data={data as ArrayPoint[]}
      summaryValue={summaryDisplayValue}
      {...merge({}, defaultCellTypeOptions, colDef?.cellTypeOptions)}
    />
  );
};
